import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Package,
  FileText,
  Tag,
  Ship,
  Building2,
  LayoutDashboard,
  Container as ContainerIcon,
  Map as MapIcon,
  ArrowRight,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCompany } from '@/contexts/CompanyContext';
import { CompanyBadge } from '@/features/dashboard/components/claude/shared';
import {
  OFFER_STATUSES,
  statusLabel,
  type Company,
} from '@/features/dashboard/data/claudeMockData';
import {
  ALL_CONTAINER_STATUSES,
  containerStatusLabel,
} from '@/features/containers/constants/statuses';
import { useSearch } from '../hooks/useSearch';
import { useSearchPalette } from '../hooks/useSearchPalette';
import type { SearchAction, SearchResult } from '../interfaces/search.interface';

const ICON_FOR_KEY: Record<string, React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
  dashboard: LayoutDashboard,
  container: ContainerIcon,
  document: FileText,
  tag: Tag,
  ship: Ship,
  package: Package,
  building: Building2,
  map: MapIcon,
};

function iconFor(name: string) {
  return ICON_FOR_KEY[name] || Package;
}

/**
 * Sintetizează un Company pentru CompanyBadge folosind datele venite de la
 * backend (companyName/companyId). Identic ca pattern cu cel din containers /
 * bills / offers, dar local — evităm o dependență cross-feature suplimentară.
 */
function buildCompany(name: string | null, id: number | null): Company | undefined {
  if (!name || id == null) return undefined;
  const palette = ['#1e6bd6', '#0c8a5b', '#b6500f', '#7a3aa8', '#0f7a8a', '#c0392b'];
  const color = palette[id % palette.length] || palette[0];
  const short =
    name
      .split(/\s+/)
      .map((p) => p.charAt(0).toUpperCase())
      .join('')
      .slice(0, 3) || name.slice(0, 3).toUpperCase();
  return { id: `co_${id}`, name, short, color, vat: '' };
}

/**
 * Badge pentru status în partea dreaptă a rezultatului. Folosește
 * ALL_CONTAINER_STATUSES / OFFER_STATUSES când backend-ul trimite `statusKind`,
 * altfel cade pe textul simplu `meta`.
 */
const StatusBadge: React.FC<{ item: SearchResult; lang: 'ro' | 'en' }> = ({ item, lang }) => {
  if (item.statusKind === 'container' && item.statusId) {
    const st = ALL_CONTAINER_STATUSES[item.statusId];
    if (st) {
      return (
        <span className={'badge ' + st.badge}>
          <span className="dot" />
          {containerStatusLabel(item.statusId, lang, item.meta)}
        </span>
      );
    }
  }
  if (item.statusKind === 'offer' && item.statusId) {
    const st = OFFER_STATUSES[item.statusId];
    if (st) {
      return (
        <span className={'badge ' + st.badge}>
          <span className="dot" />
          {statusLabel(OFFER_STATUSES, item.statusId, lang)}
        </span>
      );
    }
  }
  if (item.meta) return <span>{item.meta}</span>;
  return null;
};

/** Highlight inline al porțiunii care match-uiește query-ul. */
function Highlight({ text, q }: { text: string | null | undefined; q: string }) {
  if (!text) return null;
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const i = lower.indexOf(needle);
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + needle.length)}</mark>
      {text.slice(i + needle.length)}
    </>
  );
}

const ROUTE_FOR_PAGE: Record<string, string> = {
  dashboard: '/app/dashboard',
  containers: '/app/containers',
  bills: '/app/bills',
  offers: '/app/offers',
  map: '/app/map',
};

export const CommandPalette: React.FC = () => {
  const { isOpen, close } = useSearchPalette();
  const { currentLanguage: lang } = useLanguage();
  const { setSelectedCompanyId } = useCompany();
  const navigate = useNavigate();
  const isRo = lang === 'ro';

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const { data, isFetching } = useSearch({ enabled: isOpen, query });

  // Reset la deschidere.
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const results: SearchResult[] = data?.results || [];

  // Grupăm pentru afișare, păstrând ordinea în care apar grupurile.
  const grouped = useMemo(() => {
    const order: string[] = [];
    const map: Record<string, SearchResult[]> = {};
    for (const r of results) {
      if (!map[r.group]) {
        order.push(r.group);
        map[r.group] = [];
      }
      map[r.group].push(r);
    }
    return { order, map };
  }, [results]);

  const flatResults = useMemo(() => {
    const out: SearchResult[] = [];
    for (const g of grouped.order) out.push(...grouped.map[g]);
    return out;
  }, [grouped]);

  // ESC închide chiar și când inputul are focus (modal-ul nu mai e în viewport altfel).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => Math.min(flatResults.length - 1, a + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const sel = flatResults[active];
        if (sel) execute(sel.action);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, flatResults, active, close]);

  // Scroll itemul activ în view.
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const el = listRef.current.querySelector('.cmdk-item.active') as HTMLElement | null;
    if (!el) return;
    const cRect = listRef.current.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    if (eRect.top < cRect.top) {
      listRef.current.scrollTop -= cRect.top - eRect.top + 8;
    } else if (eRect.bottom > cRect.bottom) {
      listRef.current.scrollTop += eRect.bottom - cRect.bottom + 8;
    }
  }, [active, isOpen]);

  const execute = (action: SearchAction) => {
    close();
    setTimeout(() => {
      if (action.type === 'nav' && action.page) {
        navigate(ROUTE_FOR_PAGE[action.page] || '/app/dashboard');
      } else if (action.type === 'container' && action.id != null) {
        navigate(`/app/containers?focus=${action.id}`);
      } else if (action.type === 'bill' && action.id != null) {
        navigate(`/app/bills?focus=${action.id}`);
      } else if (action.type === 'offer' && action.id != null) {
        navigate(`/app/offers?focus=${action.id}`);
      } else if (action.type === 'company' && action.id != null) {
        setSelectedCompanyId(action.id);
      }
    }, 50);
  };

  if (!isOpen) return null;

  let flatIndex = 0;
  return (
    <div className="cmdk-backdrop" onClick={close}>
      <div className="cmdk" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk-input-wrap">
          <Search size={18} />
          <input
            ref={inputRef}
            className="cmdk-input"
            placeholder={
              isRo
                ? 'Caută containere, B/L-uri, oferte, nave, firme…'
                : 'Search containers, B/Ls, offers, vessels, companies…'
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="esc">ESC</span>
        </div>

        <div className="cmdk-results" ref={listRef}>
          {results.length === 0 ? (
            <div className="cmdk-empty">
              <div className="icon">
                <Search size={20} />
              </div>
              <div className="fw-600 fs-13">
                {isFetching
                  ? isRo
                    ? 'Se caută…'
                    : 'Searching…'
                  : isRo
                  ? 'Niciun rezultat'
                  : 'No results'}
              </div>
              {!isFetching && query && (
                <div className="muted fs-12 mt-1">
                  {isRo ? `pentru "${query}"` : `for "${query}"`}
                </div>
              )}
            </div>
          ) : (
            grouped.order.map((g) => (
              <div className="cmdk-group" key={g}>
                <div className="cmdk-group-title">{g}</div>
                {grouped.map[g].map((it) => {
                  const myIdx = flatIndex++;
                  const co = buildCompany(it.companyName, it.companyId);
                  const Icon = iconFor(it.icon);
                  return (
                    <div
                      key={it.key}
                      className={'cmdk-item' + (active === myIdx ? ' active' : '')}
                      onMouseEnter={() => setActive(myIdx)}
                      onClick={() => execute(it.action)}
                    >
                      <div className="icon">
                        <Icon size={16} />
                      </div>
                      <div className="body">
                        <div className="title">
                          <span className={it.labelMono ? 'mono' : ''}>
                            <Highlight text={it.label} q={query} />
                          </span>
                          {co && <CompanyBadge company={co} />}
                        </div>
                        {it.sub && (
                          <div className="sub">
                            <Highlight text={it.sub} q={query} />
                          </div>
                        )}
                      </div>
                      <div className="meta">
                        <StatusBadge item={it} lang={lang} />
                        {active === myIdx && <ArrowRight size={14} strokeWidth={2} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="cmdk-footer">
          <div className="hint">
            <kbd>↑</kbd>
            <kbd>↓</kbd> {isRo ? 'navighează' : 'to navigate'}
          </div>
          <div className="hint">
            <kbd>↵</kbd> {isRo ? 'deschide' : 'to open'}
          </div>
          <div className="hint">
            <kbd>esc</kbd> {isRo ? 'închide' : 'to close'}
          </div>
          <div style={{ flex: 1 }} />
          <div className="hint">
            {results.length} {isRo ? 'rezultate' : 'results'}
          </div>
        </div>
      </div>
    </div>
  );
};
